import { useCallback, useContext, useEffect, useState } from "react"

// Context
import { UserContext } from "../../../../context/userContext"

import mapboxgl from "mapbox-gl"
import Map from "react-map-gl"
import "mapbox-gl/dist/mapbox-gl.css"

//Referrals
import {
  referralEntity_GetFullAddress,
  getDistance,
  mapBoxSetFocusedLocation,
  mapBoxSetMarkers,
} from "../sharedFunctions"

//Styles
import style from "../../../../styles/referrals/ReferralEntityMapSearch.module.css"

export default function MapSearchMap({
  centerLocation,
  focusLocation,
  defaultZoom,
  markerPoints,
  refEntityShowPopup,
  fitToBounds,
  thisMap,
  setThisMap,
  triggerRefocus,
  onMove,
  onLocationSelect,
  onWheelZoom,
  showAllEntities,
  triggerResize,
  overrideHeightInPixels,
}) {
  const userCtx = useContext(UserContext)

  const [myHeight, setMyHeight] = useState(600)
  const [isLoaded, setIsLoaded] = useState(false)
  const [selectedId, setSelectedId] = useState(0)
  const selectRefIDCallBack = onLocationSelect

  const createPopUp = useCallback(
    (
      map,
      long,
      lat,
      name,
      fullAddress,
      distance,
      maxDistance,
      referralLocationID,
    ) => {
      if (map) {
        const popUps = document.getElementsByClassName("mapboxgl-popup")
        /** Check if there is already a popup on the map and if so, remove it */
        if (popUps[0]) popUps[0].remove()

        if (long && lat) {
          new mapboxgl.Popup({ closeOnClick: true })
            .setLngLat([long, lat])
            .setHTML(
              `<h3>${name}</h3>
                  ${fullAddress}
                  <h5>${
                    distance > maxDistance ? '<FONT COLOR="#ff0000"> ' : ""
                  } ${distance} miles${
                    distance > maxDistance ? "</FONT> " : ""
                  }</h5><button id="mapBoxPopupBtn" class="btn ${
                    style.mapSelectButton
                  }">Select</button>`,
            )
            .addTo(map)

          const btn = document.getElementById("mapBoxPopupBtn")
          if (btn) {
            //Note: Removed call to props.onReferralEntitySelect() from here due to the dependancy impacting the map performance
            btn.addEventListener("click", () => {
              setSelectedId(referralLocationID)
            })
          }
        }
      }
    },
    [],
  )

  useEffect(() => {
    if (!overrideHeightInPixels) {
      if (showAllEntities) {
        setMyHeight(608)
      } else {
        setMyHeight(560)
      }
    } else if (myHeight !== overrideHeightInPixels) {
      setMyHeight(overrideHeightInPixels)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllEntities, overrideHeightInPixels])

  //When myHeight changes, resize the map
  useEffect(() => {
    if (!!thisMap) {
      thisMap.resize()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myHeight, triggerResize])

  useEffect(() => {
    if (selectedId > 0) {
      const id = selectedId
      setSelectedId(0) //Reset to 0 to prevent recalls
      selectRefIDCallBack(id)
    }
  }, [selectedId, selectRefIDCallBack])

  useEffect(() => {
    if (refEntityShowPopup && thisMap) {
      let distance = 0
      const maxDistance = refEntityShowPopup.maxDistance
        ? refEntityShowPopup.maxDistance
        : 0

      if (refEntityShowPopup.distance) {
        distance = refEntityShowPopup.distance
      } else if (focusLocation) {
        distance = getDistance(
          focusLocation.longitude,
          focusLocation.latitude,
          refEntityShowPopup.longitude,
          refEntityShowPopup.latitude,
        )
      }

      createPopUp(
        thisMap,
        refEntityShowPopup.longitude,
        refEntityShowPopup.latitude,
        refEntityShowPopup.entityName,
        referralEntity_GetFullAddress(refEntityShowPopup),
        distance,
        maxDistance,
        refEntityShowPopup.locationId,
      )
    }
  }, [refEntityShowPopup, focusLocation, createPopUp, thisMap])

  useEffect(() => {
    if (focusLocation && isLoaded && thisMap) {
      mapBoxSetFocusedLocation({
        focusLocation: focusLocation,
        map: thisMap,
        markers: markerPoints,
      })
    }
  }, [
    thisMap,
    focusLocation.longitude,
    focusLocation.latitude,
    isLoaded,
    focusLocation,
    markerPoints,
  ])

  useEffect(() => {
    if (fitToBounds && thisMap) {
      if (fitToBounds.boundsBox) {
        thisMap.fitBounds(fitToBounds.boundsBox, {
          padding: 100,
          maxZoom: defaultZoom,
        })
      }

      const refEntity =
        fitToBounds.mappedReferralEntity.refEntity.referralEntity
      const refEntityLocation =
        fitToBounds.mappedReferralEntity.refEntity.referralLocation

      createPopUp(
        thisMap,
        refEntityLocation.longitude,
        refEntityLocation.latitude,
        refEntity.name,
        referralEntity_GetFullAddress(refEntityLocation),
        fitToBounds.mappedReferralEntity.distance,
        fitToBounds.mappedReferralEntity.maxDistance,
        refEntity.id,
      )
    } else if (!!thisMap && !!focusLocation) {
      thisMap.flyTo({
        center: [focusLocation.longitude, focusLocation.latitude],
      })
    }
  }, [
    fitToBounds,
    thisMap,
    createPopUp,
    triggerRefocus,
    focusLocation,
    defaultZoom,
  ])

  useEffect(() => {
    if (markerPoints && isLoaded && thisMap) {
      mapBoxSetMarkers({
        map: thisMap,
        markers: markerPoints,
        focusLocation: focusLocation,
      })
    }
  }, [thisMap, markerPoints, isLoaded, focusLocation])

  function onLoad(e) {
    setThisMap(e.target)
    setIsLoaded(true)

    return null
  }

  function flyToPoint(map, currentFeature) {
    if (currentFeature.geometry.type === "Point") {
      map.flyTo({
        center: currentFeature.geometry.coordinates,
        zoom: 10,
      })
    }
  }

  function onClick(e) {
    /* Determine if a feature in the "locations" layer exists at that point. */
    const map = e.target

    const features = map.queryRenderedFeatures(e.point, {
      layers: ["locations"],
    })

    /* If it does not exist, return */
    if (!features.length) return

    const clickedPoint = features[0]

    /* Fly to the point */
    flyToPoint(map, clickedPoint)

    /* Close all other popups and display popup for clicked store */
    if (clickedPoint.geometry.type === "Point") {
      createPopUp(
        thisMap,
        clickedPoint.geometry.coordinates[0],
        clickedPoint.geometry.coordinates[1],
        clickedPoint.properties.name,
        clickedPoint.properties.address,
        clickedPoint.properties.distance,
        clickedPoint.properties.maxDistance,
        clickedPoint.properties.id,
      )
    }

    /* Highlight listing in sidebar (and remove highlight for all other listings) */
    const activeItem = document.getElementsByClassName("active")
    if (activeItem[0]) {
      activeItem[0].classList.remove("active")
    }

    return null
  }

  return (
    <div style={{ position: "relative", top: 0, bottom: 0, width: "100%" }}>
      <Map
        mapboxAccessToken={userCtx.keys.getMapBoxKey()}
        initialViewState={{
          longitude: centerLocation.longitude,
          latitude: centerLocation.latitude,
          zoom: defaultZoom,
        }}
        longitude={centerLocation.longitude}
        latitude={centerLocation.latitude}
        style={{ height: myHeight, minHeight: "150px" }}
        mapStyle="mapbox://styles/mapbox/streets-v9"
        onMove={onMove}
        onLoad={onLoad}
        onClick={onClick}
        onWheel={onWheelZoom}
      />
    </div>
  )
}
